import {
  AllAttributesType,
  allAttrsEnum,
  LineChartTimeGroupingType,
  MetricsType,
  NullableLineChartTimeGroupingType,
  PriorityType,
  singleValueAttributes,
  WidgetDisplayOptions,
} from '@/services/api/resources/analytics-builder/constants';
import { t } from '@/ui/i18n/i18n';
import { getMonthName, destructISODateString as destructDateString } from '@/ui/helpers';
import { IssueStatus } from '@/entities/todo';
import { isEmpty } from 'lodash/fp';
import { PartialRecord } from '@/types';
import { user } from '@/ui/auth/auth';
import { getWeek, getQuarter } from 'date-fns';
import { useApprovalStatusesStore } from '@/stores/analytics/approvalStatuses';

export type ChartDataType = {
  data: Array<unknown>;
  totals?: { count: string };
  rows_before_limit_at_least?: number;
};

export type DetailedDataType = {
  data: Array<Record<'id', number> & Record<string, unknown>>;
  rows_before_limit_at_least?: number;
};

type ChartAttributeType = 'user' | 'checklist' | 'user_groups' | 'entity' | 'folders' | 'category' | 'day' | 'week'
| 'month' | 'quarter' | 'year' | 'overdue' | 'status' | 'priority' | 'sections' | 'question' | 'total' | 'average'
| 'response' | 'approval_status' | 'approval_approvers';

type NullableChartAttribute = ChartAttributeType | null;

export const getChartAttribute = (attribute: AllAttributesType | null): ChartAttributeType | null => {
  switch (attribute) {
    case allAttrsEnum.users:
    case allAttrsEnum.creator:
      return 'user';
    case allAttrsEnum.templates:
      return 'checklist';
    case allAttrsEnum.groups:
      return 'user_groups';
    case allAttrsEnum.entities:
      return 'entity';
    case allAttrsEnum.folders:
      return 'folders';
    case allAttrsEnum.categories:
      return 'category';
    case allAttrsEnum.day:
      return 'day';
    case allAttrsEnum.week:
      return 'week';
    case allAttrsEnum.month:
      return 'month';
    case allAttrsEnum.quarter:
      return 'quarter';
    case allAttrsEnum.year:
      return 'year';
    case allAttrsEnum.punctuality:
      return 'overdue';
    case allAttrsEnum['action-status']:
    case allAttrsEnum['form-status']:
      return 'status';
    case allAttrsEnum.priority:
      return 'priority';
    case allAttrsEnum['page-section']:
      return 'sections';
    case allAttrsEnum['form-item']:
      return 'question';
    case allAttrsEnum.total:
      return 'total';
    case allAttrsEnum.average:
      return 'average';
    case allAttrsEnum.response:
      return 'response';
    case allAttrsEnum['approval-status']:
      return 'approval_status';
    case allAttrsEnum.approvers:
      return 'approval_approvers';
    default:
      return null;
  }
};

const getChartValueName = (metric: MetricsType | null) => {
  if (!metric) {
    return null;
  }

  return metric.includes('score') ? 'average_score' : 'count';
};

const getDayAttributeValue = (value: string) => {
  const values = destructDateString(value);
  return new Date(values.year, values.month - 1, values.day).toLocaleDateString(user.value.locale);
};

const getWeekAttributeValue = (value: string) => {
  const values = destructDateString(value);
  const week = getWeek(new Date(values.year, values.month - 1, values.day), { weekStartsOn: 1 });
  return `W${week} ${values.year}`;
};

const getMonthAttributeValue = (value: string) => {
  const values = destructDateString(value);
  const month = getMonthName(values.month, user.value.locale, 'short');
  return `${month} ${values.year}`;
};

const getQuarterAttributeValue = (value: string) => {
  const values = destructDateString(value);
  const quarter = getQuarter(new Date(values.year, values.month - 1, values.day));
  return `Q${quarter} ${values.year}`;
};

const getYearAttributeValue = (value: string) => destructDateString(value).year;

export const getAttributeValue = (value: number|string, chartAttribute: NullableChartAttribute, attribute: AllAttributesType) => {
  const store = useApprovalStatusesStore();

  switch (chartAttribute) {
    case 'day':
      return getDayAttributeValue(value as string);
    case 'week':
      return getWeekAttributeValue(value as string);
    case 'month':
      return getMonthAttributeValue(value as string);
    case 'quarter':
      return getQuarterAttributeValue(value as string);
    case 'year':
      return getYearAttributeValue(value as string);
    case 'overdue':
      return value ? t('analytics.too-late') : t('analytics.in-time');
    case 'status':
      return attribute === 'action-status'
        ? {
          [IssueStatus.Pending]: t('status.pending'),
          [IssueStatus.Done]: t('status.done'),
          [IssueStatus.InProgress]: t('status.in-progress'),
          [IssueStatus.CantDo]: t('status.cant-do'),
        }[value]
        : value ? t('common.overdue') : t('common.due');
    case 'priority':
      return {
        [PriorityType.low]: t('common.low'),
        [PriorityType.normal]: t('common.normal'),
        [PriorityType.high]: t('common.high'),
      }[value];
    case 'approval_status':
      return store.find(Number(value))?.title;
    default:
      return value;
  }
};

const isAttributeSortable = (chartAttribute: NullableChartAttribute) => {
  return chartAttribute === 'overdue';
};

const destructAttributeData = (rawValue: never, chartAttribute: ChartAttributeType, attribute: AllAttributesType) => {
  if (typeof rawValue[chartAttribute] === 'object') {
    const entry: Record<string, string|number> = rawValue[chartAttribute];
    return {
      id: entry.keys,
      label: entry.values,
      parsedLabel: entry.values,
    };
  }

  return {
    id: rawValue[chartAttribute],
    label: rawValue[chartAttribute],
    parsedLabel: getAttributeValue(rawValue[chartAttribute], chartAttribute, attribute),
  };
};

const getY = (item: never, chartValueName: string, percentageToggled: boolean, total?: number) => {
  let y = Number(item[chartValueName]);
  if (percentageToggled && total) {
    y /= total;
    return y * 100;
  }

  return y;
};

const getMetaValue = (y: number, percentageToggled: boolean) =>
  `${percentageToggled ? y.toFixed(2) : y}${percentageToggled ? '%' : ''}`;

// TODO: For now this is not being used anymore but it might return in future features
const getTimeGroupedLineChartData = (
  rawData: Array<unknown>,
  chartAttribute: ChartAttributeType,
  attribute: AllAttributesType,
  chartValueName: string,
  lineChartTimeGrouping: LineChartTimeGroupingType,
  percentageToggled: boolean,
  total: number | undefined,
) => {
  // This check is needed to fix ClickHouse strange behavior when the query uses the WITH FILL modifier and returns
  // no rows. Only remove this check if the issue is fixed in BE.
  if (total === 0) {
    return {};
  }

  const series: Array<{id: string | number; name: string; data: unknown[]}> = [];

  rawData = rawData
    .filter(item => (item as never)[lineChartTimeGrouping] !== null)
    .sort((a, b) => (a as never)[lineChartTimeGrouping] - (b as never)[lineChartTimeGrouping]);
  const labels = [...new Set(rawData.map(item => (item as never)[lineChartTimeGrouping]))].sort();

  rawData.forEach(item => {
    const attributeItem = destructAttributeData(item as never, chartAttribute, attribute);

    const y = getY(item as never, chartValueName, percentageToggled, total);
    const seriesItemData = {
      x: (item as never)[lineChartTimeGrouping],
      y,
      meta: {
        value: getMetaValue(y, percentageToggled),
        head: attributeItem.parsedLabel,
        id: attributeItem.id,
      },
    };

    const seriesItem = series.find(currentItem =>
      typeof (item as never)[chartAttribute] === 'object'
        ? currentItem.id === attributeItem.id
        : currentItem.name === attributeItem.parsedLabel);

    seriesItem
      ? seriesItem.data.push(seriesItemData)
      : series.push({
        id: attributeItem.id,
        name: attributeItem.parsedLabel as string || ' ',
        data: [seriesItemData],
      });
  });

  return { labels, series };
};

const getAllChartData = (
  rawData: Array<unknown>,
  chartAttribute: ChartAttributeType,
  attribute: AllAttributesType,
  chartValueName: string,
  percentageToggled: boolean,
  total: number | undefined,
) => {
  // This check is needed to fix ClickHouse strange behavior when the query uses the WITH FILL modifier and returns
  // no rows. Only remove this check if the issue is fixed in BE.
  if (total === 0) {
    return {};
  }

  const labels: unknown[] = [];
  const seriesData: unknown[] = [];
  let x = 0;

  const cleanedData = rawData.filter(item => (item as never)[chartValueName] !== null);

  if (isAttributeSortable(chartAttribute)) {
    cleanedData.sort((a, b) => (a as never)[chartAttribute] - (b as never)[chartAttribute]);
  }

  cleanedData.forEach(item => {
    const attributeItem = destructAttributeData(item as never, chartAttribute, attribute!);
    labels.push(attributeItem.label);

    const y = getY(item as never, chartValueName, percentageToggled, total);
    seriesData.push({
      x,
      y,
      name: attributeItem.parsedLabel,
      meta: {
        value: getMetaValue(y, percentageToggled),
        head: attributeItem.parsedLabel,
        id: attributeItem.id,
        attribute,
      },
    });

    x++;
  });

  return {
    labels,
    series: seriesData.length
      ? [{ data: seriesData }]
      : [],
  };
};

const getSingleNumberData = (
  rawData: never,
  attribute: AllAttributesType,
  chartValueName: string,
  percentageToggled: boolean,
) => {
  const y = getY(rawData, chartValueName, percentageToggled);
  return attribute === allAttrsEnum.average ? getMetaValue(y, percentageToggled) : y;
};

const getTableData = (
  rawData: ChartDataType,
  chartAttribute: ChartAttributeType,
  attribute: AllAttributesType,
  chartValueName: string,
  percentageToggled: boolean,
  total: number | undefined,
) => {
  const tableItems: unknown[] = [];

  rawData.data.forEach(item => {
    const attributeItem = destructAttributeData(item as never, chartAttribute, attribute!);
    let value: number | string = getY(item as never, chartValueName, percentageToggled, total);
    const id = singleValueAttributes.includes(attribute!) ? 0 : attributeItem.id;
    const label = singleValueAttributes.includes(attribute!)
      ? t(`common.${chartAttribute}`)
      : attributeItem.parsedLabel;

    value = getMetaValue(value, percentageToggled);

    tableItems.push({
      id,
      label,
      value,
    });
  });

  return {
    items: tableItems,
    totalRows: rawData.rows_before_limit_at_least ?? 1,
  };
};

export const parseWidgetData = (
  rawData: ChartDataType,
  attribute: AllAttributesType | null,
  metric: MetricsType | null,
  lineChartTimeGrouping: NullableLineChartTimeGroupingType,
  selectedDisplay: WidgetDisplayOptions | null,
  percentageToggled: boolean,
) => {
  const chartAttribute = getChartAttribute(attribute);
  const chartValueName = getChartValueName(metric);

  if (!(chartAttribute && chartValueName)) {
    return {};
  }

  const total = rawData.totals?.count ? Number(rawData.totals.count) : undefined;

  switch (true) {
    case selectedDisplay === WidgetDisplayOptions.singleNumber:
      return getSingleNumberData(rawData.data[0] as never, attribute!, chartValueName, percentageToggled);
    case selectedDisplay === WidgetDisplayOptions.table:
      return attribute === allAttrsEnum.response
        ? parseDetailedViewData(rawData as DetailedDataType)
        : getTableData(rawData, chartAttribute, attribute!, chartValueName, percentageToggled, total);
    case selectedDisplay === WidgetDisplayOptions.barChart:
    case selectedDisplay === WidgetDisplayOptions.lineChart:
    case selectedDisplay === WidgetDisplayOptions.pieChart:
      return getAllChartData(
        rawData.data,
        chartAttribute,
        attribute!,
        chartValueName,
        percentageToggled,
        total,
      );
    default:
      return {};
  }
};

export const parseDetailedViewData = (rawData: DetailedDataType) => {
  type FIELD = 'id' | 'user' | 'entity' | 'percentage' | 'status' | 'title' | 'conducted_at_timestamp'
  | 'updated_at_timestamp' | 'due_at_timestamp' | 'assignees' | 'priority' | 'user_groups' | 'response'
  | 'inspection_id' | 'inspection_title' | 'notes' | 'breadcrumb' | 'type' | 'approval_status' | 'approval_approvers';
  const items: Array<PartialRecord<FIELD, unknown>> = [];
  const OBJECT_FIELDS: FIELD[] = ['user', 'entity'];
  const OBJECT_ARRAY_FIELDS: FIELD[] = ['assignees', 'user_groups', 'approval_approvers'];
  const NUMBER_FIELDS: FIELD[] = ['percentage', 'status', 'priority', 'inspection_id', 'approval_status'];
  const STRING_FIELDS: FIELD[] = ['title', 'conducted_at_timestamp', 'due_at_timestamp', 'updated_at_timestamp',
    'response', 'inspection_title', 'notes', 'type'];
  const ARRAY_STRING_FIELDS: FIELD[] = ['breadcrumb'];

  rawData.data.forEach(item => {
    const dataItem: PartialRecord<FIELD, unknown> = {};
    dataItem.id = item.id;

    OBJECT_FIELDS.forEach(field => {
      if (!isEmpty(item[field])) {
        const [key, value] = Object.entries(item[field]!)[0];
        dataItem[field] = { id: key, value };
      }
    });

    OBJECT_ARRAY_FIELDS.forEach(field => {
      if (field in item) {
        dataItem[field] = Object.entries(item[field]!).map(([id, value]) => ({ id, value }));
      }
    });

    NUMBER_FIELDS.concat(STRING_FIELDS).forEach(field => {
      if (field in item) {
        dataItem[field] = item[field];
      }
    });

    ARRAY_STRING_FIELDS.forEach(field => {
      if (field in item) {
        dataItem[field] = item[field];
      }
    });

    items.push(dataItem);
  });

  return {
    items,
    totalRows: rawData.rows_before_limit_at_least ?? 1,
  };
};
